import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { lawsuitsApi } from "@/features/worksheets/api";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { centsToDollars, dollarsToCents } from "@/features/worksheets/money";
import type { Lawsuit } from "@/features/worksheets/types";

interface LawsuitFormValues {
  description: string;
  amount: string;
  dateFiled: string;
}

const validationSchema = Yup.object({
  description: Yup.string().required("Description is required"),
});

function toInitialValues(record: Lawsuit | null): LawsuitFormValues {
  return {
    description: record?.description ?? "",
    amount: record ? centsToDollars(record.amount) : "",
    dateFiled: record?.dateFiled ?? "",
  };
}

function toPayload(v: LawsuitFormValues): Record<string, unknown> {
  return {
    description: v.description,
    amount: dollarsToCents(v.amount),
    dateFiled: v.dateFiled || null,
  };
}

export function LawsuitDialog({
  caseId,
  record,
  open,
  onOpenChange,
  onSaved,
}: {
  caseId: string;
  record: Lawsuit | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}) {
  async function onSave(values: LawsuitFormValues) {
    const payload = toPayload(values);
    if (record) {
      await lawsuitsApi.update(caseId, record.id, payload);
    } else {
      await lawsuitsApi.create(caseId, payload);
    }
  }

  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Lawsuit"
      initialValues={toInitialValues(record)}
      validationSchema={validationSchema}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField
        name="description"
        label="Description"
        labelClassName={FIELD_LABEL}
        placeholder="e.g. Civil suit, plaintiff / defendant"
      />
      <div className="grid grid-cols-2 gap-4">
        <MoneyField name="amount" label="Amount" />
        <FormField
          name="dateFiled"
          label="Date Filed"
          labelClassName={FIELD_LABEL}
          type="date"
        />
      </div>
    </CrudDialog>
  );
}
